"use client";

import { useEffect, useRef, useState } from "react";
import { IconClock } from "@/components/ui/icons";
import { useOutsideClick } from "@/lib/use-outside-click";

type TimePickerProps = {
  value: string;          // "HH:MM" or ""
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  minuteStep?: number;    // 1 / 5 / 15 etc.
  align?: "left" | "right";
};

export function TimePicker({
  value,
  onChange,
  placeholder = "Выбрать время",
  disabled = false,
  size = "sm",
  minuteStep = 1,
  align = "left",
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  const valid = /^\d{2}:\d{2}$/.test(value);
  const [hh, mm] = valid ? value.split(":") : ["", ""];

  useOutsideClick(ref, () => setOpen(false), open);

  // Center the selected hour/minute when the popover opens. We scroll the
  // column itself (not the page) to avoid disrupting the rest of the layout.
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      for (const col of [hoursRef.current, minutesRef.current]) {
        if (!col) continue;
        const sel = col.querySelector<HTMLElement>("[data-selected=true]");
        if (sel) {
          col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.clientHeight / 2;
        }
      }
    });
  }, [open]);

  function selectHour(h: string) {
    onChange(`${h}:${mm || "00"}`);
  }
  function selectMinute(m: string) {
    onChange(`${hh || "00"}:${m}`);
  }

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => String(i * minuteStep).padStart(2, "0"),
  );

  const triggerClass = size === "sm"
    ? "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[11px] text-[var(--text)] hover:border-[var(--primary)]/50 transition focus:outline-none disabled:opacity-50"
    : "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[12px] text-[var(--text)] hover:border-[var(--primary)]/50 transition focus:outline-none disabled:opacity-50";

  return (
    <div ref={ref} className="relative inline-block w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={`${triggerClass} w-full`}
      >
        <IconClock size={size === "sm" ? 12 : 14} className="shrink-0 text-[var(--textMuted)]" />
        {value ? (
          <span className="flex-1 text-left tabular-nums">{value}</span>
        ) : (
          <span className="flex-1 text-left text-[var(--textMuted)]">{placeholder}</span>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full z-50 mt-1 w-[150px] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] elev-modal overflow-hidden ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          <div className="grid grid-cols-2 divide-x divide-[var(--border)]">
            <div
              ref={hoursRef}
              className="max-h-[220px] overflow-y-auto py-1 scroll-thin"
            >
              {hours.map(h => {
                const sel = h === hh;
                return (
                  <button
                    key={h}
                    data-selected={sel}
                    type="button"
                    onClick={() => selectHour(h)}
                    className={`block w-full px-2 py-1 text-[12px] tabular-nums transition ${
                      sel
                        ? "bg-[var(--primary)] text-[var(--primaryText)] font-semibold"
                        : "text-[var(--text)] hover:bg-[var(--hover)]"
                    }`}
                  >
                    {h}
                  </button>
                );
              })}
            </div>
            <div
              ref={minutesRef}
              className="max-h-[220px] overflow-y-auto py-1 scroll-thin"
            >
              {minutes.map(m => {
                const sel = m === mm;
                return (
                  <button
                    key={m}
                    data-selected={sel}
                    type="button"
                    onClick={() => selectMinute(m)}
                    className={`block w-full px-2 py-1 text-[12px] tabular-nums transition ${
                      sel
                        ? "bg-[var(--primary)] text-[var(--primaryText)] font-semibold"
                        : "text-[var(--text)] hover:bg-[var(--hover)]"
                    }`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="border-t border-[var(--border)] px-3 py-1.5 text-right">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] text-[var(--textMuted)] hover:text-[var(--text)] transition"
            >
              Готово
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
